/**
 * Did approving a card actually do the thing?
 *
 * Approval is the whole trust model: the owner taps once, and Mucka does what
 * the card said. A handler can refuse for good reasons — the slot went, the
 * client opted out, the provider rejected it — and when that happens the card
 * must stay put and say so. It used to vanish silently: an owner approved four
 * receptionist bookings on his phone, no jobs were created, and nothing told
 * him. He reported the receptionist as broken.
 *
 * Every surface that approves a card routes its check through here so the rule
 * can't drift between desktop and mobile. `/api/actions/[id]` answers a refused
 * execution with 422 + `{ ok: false, error, execution }`, so both the
 * status-code check and the body check catch it.
 */

/** Shown when the server gave us nothing useful. Plain English, no jargon. */
export const APPROVAL_FALLBACK_MESSAGE =
  "That didn't work, so nothing has changed. Give it another go in a minute.";

export function parseApprovalResponse(
  resOk: boolean,
  body: unknown,
): { failed: boolean; message: string } {
  const parsed = (body && typeof body === 'object' ? body : {}) as {
    ok?: boolean;
    error?: unknown;
    execution?: { success?: boolean; message?: unknown } | null;
  };

  const execution = parsed.execution || null;
  const failed =
    !resOk ||
    parsed.ok === false ||
    (execution ? execution.success === false : false);

  if (!failed) return { failed: false, message: '' };

  // Prefer the handler's own wording — it's already written for the owner
  // ("That slot has been taken since the call, so the job was not created.").
  const fromExecution = typeof execution?.message === 'string' ? execution.message.trim() : '';
  const fromError = typeof parsed.error === 'string' ? parsed.error.trim() : '';

  return { failed: true, message: fromExecution || fromError || APPROVAL_FALLBACK_MESSAGE };
}
